import { ExternalLink, Linkedin } from 'lucide-react';
import * as React from 'react';

import {
  NO_SOCIAL_CHANNELS,
  SOCIAL_CHANNELS_DESCRIPTION,
  SOCIAL_CHANNELS_LABEL,
} from './constants';
import type { SocialChannel } from './types';

/**
 * Social channels (`MEM-033` AC-5) — **the one place in the product these render**.
 *
 * `MEM-012` AC-6 puts them behind an accepted connection, and the API holds the other half of the
 * rule: a preview card and an attendee profile carry no channel field at all, because until this
 * slice existed there was no reader who qualified. There is one now, and this is the panel.
 *
 * ## What this panel is for, and what it stopped being on 2026-09-11
 *
 * It used to say these were the **only** route two connected members had to each other — ADR 032
 * removed in-app messaging from MVP, `MEM-033`'s own note said AC-5 *"carries more weight than it
 * did when this was written"*, and the sentence under the heading existed to tell a member who
 * was looking for a message button where the conversation was supposed to happen instead.
 *
 * ADR 052 supersedes ADR 032, `MEM-054` builds the thread and `MEM-055` builds the Messages page,
 * so the member looking for a message button now finds one — three items along the navigation,
 * and a Messages card at the top of this very screen. ADR 052 says what this panel becomes:
 * *"Social channels go back to being a convenience … They stay, on the connection detail, and
 * stop carrying that load."* {@link SOCIAL_CHANNELS_DESCRIPTION} was reworded to match; this
 * header is corrected in the same sweep.
 *
 * ## Only LinkedIn exists
 *
 * `MEM-008` owns the channel vocabulary — LinkedIn, X, Instagram, Website — and is not built;
 * `linkedIn` is the one field on a member registration row. The panel is a list rather than a
 * LinkedIn row so that story adds entries rather than a component, and the empty state is
 * phrased about *them* not having added channels rather than about the platform not supporting
 * any, because from the member's side those are the same thing today and only one of them stays
 * true.
 *
 * `rel="noreferrer"` on an outbound link: the destination is a member's own profile elsewhere,
 * and it does not need to be told which H2HSpark screen sent the visit.
 */
export function SocialChannelsPanel({
  channels,
}: {
  readonly channels: ReadonlyArray<SocialChannel>;
}): React.ReactElement {
  return (
    <div className="space-y-2">
      {/* §2.11's three right-hand cards head themselves the same way — an `<h2>` at
          `var(--text-lg)` over a muted sentence — so this one does too. It used to be the
          uppercase micro-label the phone screen's sections wore. */}
      <h2 className="text-lg">{SOCIAL_CHANNELS_LABEL}</h2>
      <p className="text-sm leading-snug text-muted-foreground">{SOCIAL_CHANNELS_DESCRIPTION}</p>
      {channels.length === 0 ? (
        <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
          {NO_SOCIAL_CHANNELS}
        </p>
      ) : (
        <ul className="space-y-2">
          {channels.map((channel) => (
            <li key={channel.channel}>
              <a
                href={channel.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-[11px] rounded-md bg-ivory-100 px-3.5 py-[11px] transition-colors hover:bg-brand-50"
              >
                <span
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand"
                  aria-hidden
                >
                  <Linkedin className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-brand text-md font-bold text-foreground">
                    {channel.label}
                  </span>
                  <span className="block truncate text-sm text-muted-foreground">
                    {channel.url}
                  </span>
                </span>
                <ExternalLink className="h-4 w-4 shrink-0 text-text-subtle" aria-hidden />
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
